import { getDefaultStateForCountry } from "utilities";

export interface WebformFormState {
  webformId: number;

  clientId: number;

  titleInternal: string;
  header: server.dto.TranslationValue[];
  description: server.dto.TranslationValue[];

  displayEventNote: boolean;
  eventNoteLabel: server.dto.TranslationValue[];

  nameOfEvent: server.dto.FormElementBehaviors;
  timeOfPerformance: server.dto.FormElementBehaviors;
  eventWebsite: server.dto.FormElementBehaviors;
  ticketPrice: server.dto.FormElementBehaviors;

  financialOffer: server.dto.FormElementBehaviors;
  financialOfferDescription: server.dto.TranslationValue[];
  availableCurrencies: server.dto.Currency[];

  defaultBookkeeping: server.dto.BookkeepingSmall;
  defaultStatus: server.dto.StatusSmall;
  defaultCountry: server.dto.Country;
  defaultUser: server.dto.UserSmall;
  defaultCulture: server.dto.Culture;
  availableCultures: server.dto.Culture[];

  displayDataProcessingDisclaimer: boolean;
  dataProcessingDisclaimerText: server.dto.TranslationValue[];

  pageMode: server.dto.BookingRequestPageModes;
  customCss: string;

  descriptionSuccess: server.dto.TranslationValue[];
  recipientEmails: string;
}

export const getUpdateInstruction = (state: WebformFormState): server.dto.UpdateWebformInstruction => ({
  webformId: state.webformId,
  titleInternal: state.titleInternal,
  header: state.header,
  description: state.description,

  displayEventNote: state.displayEventNote,
  eventNoteLabel: state.eventNoteLabel,

  eventName: state.nameOfEvent,
  eventTimeOfPerformance: state.timeOfPerformance,
  eventWebsite: state.eventWebsite,
  ticketPrice: state.ticketPrice,

  financialOffer: state.financialOffer,
  financialOfferDescription: state.financialOfferDescription,
  availableCurrencies: state.availableCurrencies.map(currency => currency.value),

  defaultBookkeepingId: state.defaultBookkeeping.bookkeepingId,
  defaultStatusId: state.defaultStatus.statusId,
  defaultStateId: getDefaultStateForCountry(state.defaultCountry.key, serverReference).key,
  defaultUser: state.defaultUser.userId,
  defaultCulture: state.defaultCulture.value,
  availableCultures: state.availableCultures.map(culture => culture.value),

  displayDataProcessingDisclaimer: state.displayDataProcessingDisclaimer,
  dataProcessingDisclaimerText: state.dataProcessingDisclaimerText,

  pageMode: state.pageMode,
  customCss: state.customCss,

  descriptionSuccess: state.descriptionSuccess,
  recipientEmails: state.recipientEmails,
});

export interface WebformPromoterFromState {
  webformId: number;

  header: server.dto.TranslationValue[];
  description: server.dto.TranslationValue[];

  contactAddress: server.dto.FormElementBehaviors;
  contactPosition: server.dto.FormElementBehaviors;
  contactMobile: server.dto.FormElementBehaviors;
  contactFacebook: server.dto.FormElementBehaviors;
  contactNationality: server.dto.FormElementBehaviors;
  contactDateOfBirth: server.dto.FormElementBehaviors;
  contactPassportNumber: server.dto.FormElementBehaviors;
  contactSocialSecurityNumber: server.dto.FormElementBehaviors;
  contactAdditionalInformation: boolean;

  companyAddressLine1: server.dto.FormElementBehaviors;
  companyAddressLine2: server.dto.FormElementBehaviors;
  companyPostalCode: server.dto.FormElementBehaviors;
  companyCity: server.dto.FormElementBehaviors;
  companyPhone: server.dto.FormElementBehaviors;
  companyWebsite: server.dto.FormElementBehaviors;
  companyFacebook: server.dto.FormElementBehaviors;
  companyTaxNumber: server.dto.FormElementBehaviors;
  companyBusinessNumber: server.dto.FormElementBehaviors;
  companyAdditionalInformation: boolean;
}

export const getUpdateWebformPromoterInstruction = (state: WebformPromoterFromState): server.dto.UpdateWebformPromoterFunctionGroupInstruction => ({
  webformId: state.webformId,

  header: state.header,
  description: state.description,

  contact: {
    display: true,
    address: state.contactAddress,
    position: state.contactPosition,
    mobile: state.contactMobile,
    facebook: state.contactFacebook,
    nationality: state.contactNationality,
    birthDate: state.contactDateOfBirth,
    passportNumber: state.contactPassportNumber,
    socialSecurity: state.contactSocialSecurityNumber,
    additional: state.contactAdditionalInformation,
  },

  company: {
    address1: state.companyAddressLine1,
    address2: state.companyAddressLine2,
    postalCode: state.companyPostalCode,
    city: state.companyCity,
    phone: state.companyPhone,
    website: state.companyWebsite,
    facebook: state.companyFacebook,
    taxNumber: state.companyTaxNumber,
    businessRegistrationNumber: state.companyBusinessNumber,
    additional: state.companyAdditionalInformation,
  }
});

export interface WebformVenueFromState {
  webformId: number;

  header: server.dto.TranslationValue[];
  description: server.dto.TranslationValue[];

  contactDisplay: boolean;
  contactAddress: server.dto.FormElementBehaviors;
  contactPosition: server.dto.FormElementBehaviors;
  contactMobile: server.dto.FormElementBehaviors;
  contactFacebook: server.dto.FormElementBehaviors;
  contactNationality: server.dto.FormElementBehaviors;
  contactDateOfBirth: server.dto.FormElementBehaviors;
  contactPassportNumber: server.dto.FormElementBehaviors;
  contactSocialSecurityNumber: server.dto.FormElementBehaviors;
  contactAdditionalInformation: boolean;

  companyAddressLine1: server.dto.FormElementBehaviors;
  companyAddressLine2: server.dto.FormElementBehaviors;
  companyPostalCode: server.dto.FormElementBehaviors;
  companyCity: server.dto.FormElementBehaviors;
  companyPhone: server.dto.FormElementBehaviors;
  companyWebsite: server.dto.FormElementBehaviors;
  companyFacebook: server.dto.FormElementBehaviors;
  companyTaxNumber: server.dto.FormElementBehaviors;
  companyBusinessNumber: server.dto.FormElementBehaviors;
  companyAdditionalInformation: boolean;

  venueCapacity: server.dto.FormElementBehaviors;
  venueAgeRestrictions: server.dto.FormElementBehaviors;
  venueDoors: server.dto.FormElementBehaviors;
  venueCategory: server.dto.FormElementBehaviors;
  venueStage: server.dto.FormElementBehaviors;
  venueVechicleAccess: server.dto.FormElementBehaviors;
  venueParking: server.dto.FormElementBehaviors;
  venueGearAccess: server.dto.FormElementBehaviors;
  venuePower: server.dto.FormElementBehaviors;
  venueLighting: server.dto.FormElementBehaviors;
  venueDressingRooms: server.dto.FormElementBehaviors;
  venueNoiseRestrictions: server.dto.FormElementBehaviors;
}

export const getUpdateWebformVenueInstruction = (state: WebformVenueFromState): server.dto.UpdateWebformVenueFunctionGroupInstruction => ({
  webformId: state.webformId,

  header: state.header,
  description: state.description,

  contact: {
    display: state.contactDisplay,
    address: state.contactAddress,
    position: state.contactPosition,
    mobile: state.contactMobile,
    facebook: state.contactFacebook,
    nationality: state.contactNationality,
    birthDate: state.contactDateOfBirth,
    passportNumber: state.contactPassportNumber,
    socialSecurity: state.contactSocialSecurityNumber,
    additional: state.contactAdditionalInformation,
  },

  company: {
    address1: state.companyAddressLine1,
    address2: state.companyAddressLine2,
    postalCode: state.companyPostalCode,
    city: state.companyCity,
    phone: state.companyPhone,
    website: state.companyWebsite,
    facebook: state.companyFacebook,
    taxNumber: state.companyTaxNumber,
    businessRegistrationNumber: state.companyBusinessNumber,
    additional: state.companyAdditionalInformation,
  },

  venue: {
    capacity: state.venueCapacity,
    ageRestrictions: state.venueAgeRestrictions,
    doors: state.venueDoors,
    category: state.venueCategory,
    stage: state.venueStage,
    vehicleAccess: state.venueVechicleAccess,
    parking: state.venueParking,
    gearAccess: state.venueGearAccess,
    power: state.venuePower,
    lighting: state.venueLighting,
    dressingRooms: state.venueDressingRooms,
    noiseRestrictions: state.venueNoiseRestrictions,
  }
});

export interface WebformArtistFromState {
  webformId: number;

  header: server.dto.TranslationValue[];
  description: server.dto.TranslationValue[];
}

export const getUpdateWebformArtistInstruction = (state: WebformArtistFromState): server.dto.UpdateWebformArtistFunctionGroupInstruction => ({
  webformId: state.webformId,

  header: state.header,
  description: state.description,
});